import React, { useState, useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Alert, Table, Form, FormGroup, Label, Input, Row, Col } from "reactstrap";
import Loading from "../components/Loading";
import OrgHeader from "../components/OrgHeader";
import RoleBadge from "../components/RoleBadge";
import { getOrganisation, readSelfProfile, addOrganisationMember, removeOrganisationMember, assignMemberRole, removeMemberRole } from "../utils/apiService";
import { canManageMembers, roleFor } from "../utils/orgRoles";
import { ROLE_TYPES } from "../utils/orgConstants";

const OrganisationMembersComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addForm, setAddForm] = useState({
    emailAddress: "",
    firstName: "",
    lastName: "",
  });
  const [addingMember, setAddingMember] = useState(false);

  useEffect(() => {
    loadOrganisation();
  }, [id]);

  const loadOrganisation = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const [data, self] = await Promise.all([getOrganisation(id, token), readSelfProfile(token)]);

      // The API returns no role; the creator is the Owner (see roleFor).
      const role = roleFor(data, self?.userId);
      setUserRole(role);

      if (!canManageMembers(role)) {
        history.push(`/organisations/${id}`);
        return;
      }

      setOrg({
        id: data?.id || id,
        name: data?.name || data?.orgName || 'Unknown',
        identifier: data?.identifier || data?.orgIdentifier || '',
      });

      const membersList = data?.members || [];
      setMembers(membersList);
    } catch (err) {
      setError(err?.message || "Failed to load organisation");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError("");
    setAddingMember(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      await addOrganisationMember(id, addForm, token);
      setAddForm({ emailAddress: "", firstName: "", lastName: "" });
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName}?`)) {
      return;
    }

    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      await removeOrganisationMember(id, memberId, token);
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to remove member");
    }
  };

  const handleRoleChange = async (memberId, newRoleId, currentRoleId) => {
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);

      // Remove current role if exists
      if (currentRoleId) {
        await removeMemberRole(id, memberId, currentRoleId, token);
      }

      // Assign new role
      await assignMemberRole(id, memberId, newRoleId, token);
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to change member role");
    }
  };

  const isOnlyOwner = (member) => {
    const ownerCount = members.filter(m => m.role === 'Owner').length;
    return member.role === 'Owner' && ownerCount === 1;
  };

  if (loading) {
    return <Loading />;
  }

  if (!canManageMembers(userRole)) {
    return (
      <Alert color="warning">
        You do not have permission to manage members.
        <div className="mt-3">
          <Button color="secondary" onClick={() => history.push(`/organisations/${id}`)}>
            Back to Details
          </Button>
        </div>
      </Alert>
    );
  }

  if (!org) {
    return <Alert color="warning">Organisation not found</Alert>;
  }

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <OrgHeader id={id} name={org.name} role={userRole} identifier={org.identifier} />
      <p className="tab-intro">Who can manage {org.name} and its offers.</p>

      {error && <Alert color="danger" className="mb-3">{error}</Alert>}

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">Add member</div>
        </div>
        <div className="panel-body">
          <Form onSubmit={handleAddMember}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="emailAddress">Email Address *</Label>
                  <Input
                    id="emailAddress"
                    name="emailAddress"
                    type="email"
                    value={addForm.emailAddress}
                    onChange={(e) => setAddForm(prev => ({ ...prev, emailAddress: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={addForm.firstName}
                    onChange={(e) => setAddForm(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={addForm.lastName}
                    onChange={(e) => setAddForm(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <FormGroup className="w-100">
                  <Button color="primary" type="submit" block disabled={addingMember}>
                    {addingMember ? 'Adding...' : 'Add'}
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">{members.length} member{members.length === 1 ? "" : "s"}</div>
        </div>
          {members.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <div className="empty-title">No members yet</div>
              <div className="empty-text">Add the first member above.</div>
            </div>
          ) : (
            <Table responsive className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const memberName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
                  const onlyOwner = isOnlyOwner(member);

                  return (
                    <tr key={member.id || member.memberId}>
                      <td className="cell-strong">{memberName}</td>
                      <td className="cell-muted">{member.email || member.emailAddress}</td>
                      <td>
                        <RoleBadge role={member.role} />
                      </td>
                      <td>
                        <div className="row-actions" style={{ justifyContent: "flex-start" }}>
                          <Input
                            type="select"
                            size="sm"
                            style={{ width: '150px' }}
                            value={member.roleId || ''}
                            onChange={(e) => handleRoleChange(member.id || member.memberId, parseInt(e.target.value), member.roleId)}
                          >
                            <option value="">Select Role</option>
                            <option value="1">Owner</option>
                            <option value="2">Admin</option>
                            <option value="3">Member</option>
                          </Input>
                          <button
                            className="btn-pill btn-pill-red"
                            onClick={() => handleRemoveMember(member.id || member.memberId, memberName)}
                            disabled={onlyOwner}
                            title={onlyOwner ? 'Cannot remove the only owner' : 'Remove member'}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
      </div>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationMembersComponent, {
  onRedirecting: () => <Loading />,
});
